import React, { useState } from "react";
import { CheckCircle2, Coins, Sparkles } from "lucide-react";
import { Slider } from "./ui/slider";
import { toast } from "sonner";
import { PRICING_PLANS, addCredits } from "../mock/mock";

const PricingSection = ({ compact = false }) => {
  const [customCredits, setCustomCredits] = useState(100);

  const buy = (credits) => {
    // COMPRA SIMULADA (mock): suma créditos en local, sin pago real
    addCredits(credits);
    toast.success(`${credits.toLocaleString("es-ES")} créditos añadidos a tu cuenta (compra de demostración)`);
  };

  return (
    <section id="tarifas" className={compact ? "" : "py-20 sm:py-24 bg-[#FFF7F0]"}>
      <div className="max-w-[1240px] mx-auto px-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-12">
          <div>
            <p className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FDEBDC] text-[#C2570F] text-[12px] font-extrabold uppercase tracking-wider mb-4">
              <Sparkles size={12} /> Tarifas
            </p>
            <h2 className="font-display text-[30px] sm:text-[40px] font-extrabold text-[#241E18] leading-tight">
              Paga solo por lo que difuminas
            </h2>
          </div>
          <p className="text-[14px] text-[#8A7F73] max-w-sm">
            1 crédito = 1 segundo de vídeo procesado. Los créditos nunca caducan y todos los planes incluyen prueba gratuita.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
          {PRICING_PLANS.map((plan) => (
            <div
              key={plan.name}
              className={`relative flex flex-col rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1.5 ${
                plan.popular
                  ? "bg-[#F26F21] text-white shadow-2xl shadow-[#F26F21]/30"
                  : "bg-white border border-[#F0E7DD] hover:shadow-xl hover:shadow-[#F26F21]/10"
              }`}
            >
              {plan.popular && (
                <span className="absolute -top-3 right-6 px-3.5 py-1 rounded-full bg-[#241E18] text-[#FFB81C] text-[11.5px] font-extrabold tracking-wide">
                  El más elegido
                </span>
              )}
              <h3 className={`font-display text-[21px] font-extrabold ${plan.popular ? "text-white" : "text-[#241E18]"}`}>{plan.name}</h3>
              <p className={`text-[13px] mt-1 ${plan.popular ? "text-white/75" : "text-[#8A7F73]"}`}>{plan.desc}</p>
              <div className="mt-5 flex items-baseline gap-1">
                <span className="font-display text-[40px] font-extrabold">{plan.price}</span>
                <span className={`text-[17px] font-bold ${plan.popular ? "text-white/80" : "text-[#8A7F73]"}`}>€</span>
              </div>
              <ul className="mt-5 space-y-2.5 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className={`flex items-start gap-2.5 text-[13.5px] ${plan.popular ? "text-white/90" : "text-[#57503F]"}`}>
                    <CheckCircle2 size={16} className={`mt-0.5 shrink-0 ${plan.popular ? "text-[#FFB81C]" : "text-[#F26F21]"}`} />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => buy(plan.credits)}
                className={`mt-7 w-full h-11 rounded-full text-[14px] font-bold transition-all ${
                  plan.popular
                    ? "bg-white text-[#F26F21] hover:bg-[#FFF3E9]"
                    : "bg-[#241E18] text-white hover:bg-[#F26F21]"
                }`}
              >
                Conseguir créditos
              </button>
            </div>
          ))}

          {/* Plan a medida con slider */}
          <div className="flex flex-col rounded-3xl bg-white border border-[#F0E7DD] p-7 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-[#F26F21]/10">
            <h3 className="font-display text-[21px] font-extrabold text-[#241E18]">A tu medida</h3>
            <p className="text-[13px] text-[#8A7F73] mt-1">Elige los créditos exactos que necesitas</p>
            <div className="mt-5 flex items-baseline gap-1">
              <span className="font-display text-[40px] font-extrabold text-[#241E18]">
                {(customCredits * 0.1).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <span className="text-[17px] font-bold text-[#8A7F73]">€</span>
            </div>
            <div className="mt-4 flex-1">
              <Slider
                value={[customCredits]}
                min={100}
                max={10000}
                step={100}
                onValueChange={(v) => setCustomCredits(v[0])}
                className="[&_[role=slider]]:bg-[#F26F21] [&_[role=slider]]:border-[#F26F21] [&_.bg-primary]:bg-[#F26F21]"
              />
              <div className="flex justify-between text-[11.5px] text-[#A69C90] mt-2 font-semibold">
                <span>100</span>
                <span>10.000</span>
              </div>
              <div className="mt-4 rounded-2xl bg-[#FFF3E9] px-4 py-3 flex items-center gap-2 text-[13.5px] font-bold text-[#C2570F]">
                <Coins size={15} />
                {customCredits.toLocaleString("es-ES")} créditos
                <span className="font-medium text-[#C2570F]/70">· ~{Math.round(customCredits / 60)} min</span>
              </div>
            </div>
            <button
              onClick={() => buy(customCredits)}
              className="mt-7 w-full h-11 rounded-full bg-[#241E18] text-white text-[14px] font-bold hover:bg-[#F26F21] transition-all"
            >
              Conseguir créditos
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
